import {
  CSSObject,
  Components,
  Theme,
  chipClasses,
  formControlLabelClasses,
  inputLabelClasses,
  outlinedInputClasses,
  radioClasses,
  switchClasses,
} from '@mui/material';
import { listItemTextClasses } from '@mui/material/ListItemText';
import { paginationItemClasses } from '@mui/material/PaginationItem';
import { stepIconClasses } from '@mui/material/StepIcon';
import { tabClasses } from '@mui/material/Tab';
import {
  blue,
  blueGrey,
  cyan,
  deepPurple,
  green,
  indigo,
  lightGreen,
  orange,
  pink,
  purple,
  red,
} from '@mui/material/colors';
import { SimplePaletteColorOptions, alpha, createTheme, lighten } from '@mui/material/styles';
import { menuButtonClasses, menuSelectClasses } from 'mui-tiptap';

import { LinkBehavior } from '../components/LinkBehavior';
import i18n from '../i18n';
import { FG_IMPLICIT_LIST_CLASS } from '../utils/enhanceImplicitListsInHtml';
import { FG_TABLE_SR_ONLY_CAPTION_CLASS } from '../utils/enhanceTablesInHtml';
import { focusFirstFocusable } from '../utils/focusFirstFocusable';
import { ACCESSIBLE_SUBTEXT_ON_INTERACTIVE } from '../utils/gradeBadgeColors';
import { getAppRootModalContainer } from '../utils/muiAppRootModalContainer';
import { screenReaderOnlyLabelSx } from '../utils/screenReaderOnlyLabelSx';
import { COLOR_OPTIONS, HTML_EDITOR_CLASSES, MUI_STATE_CLASSES } from './constants';
import { primaryOutlinedButtonAccessibleVars } from './primaryOutlinedPrimaryButtonA11y';
import { GradientVariant, MuiIconButtonVariant } from './types';
import {
  augmentTheme,
  getFilledIconButtonStyles,
  getOutlinedIconButtonStyles,
  isMuiColor,
} from './utils';

const GRADIENT_COLORS: Record<GradientVariant, SimplePaletteColorOptions> = {
  blue: { main: blue[900] },
  cyan: { main: indigo[800] },
  green: { main: lightGreen[900] },
  grey: { main: blueGrey[800] },
  indigo: { main: deepPurple[400] },
  light: { main: lighten(purple[800], 0.8) },
  orange: { main: orange[900] },
  pink: { main: pink[900] },
  purple: { main: deepPurple[800] },
  red: { main: red[800] },
  teal: { main: cyan[900] },
};

function primaryTextButtonAccessibleVars(theme: Theme): CSSObject {
  const dark = theme.palette.primary.dark;
  const hoverBg = theme.alpha(dark, theme.palette.action.hoverOpacity);
  const hoverStyles = {
    '--variant-textColor': dark,
    '--variant-textBg': hoverBg,
    color: `${dark} !important`,
    backgroundColor: `${hoverBg} !important`,
  };
  return {
    '--variant-textColor': dark,
    color: dark,
    '&:hover': hoverStyles,
    '@media (hover: hover)': {
      '&:hover': hoverStyles,
    },
  };
}

const OUTLINED_INPUT_BORDER = '#BDBDBD';

function muiInputPlaceholderAccessible(theme: Theme): CSSObject {
  const ph: CSSObject = {
    opacity: 1,
    color: theme.palette.text.secondary,
  };
  return {
    '&&::placeholder': ph,
    '&&::-webkit-input-placeholder': ph,
    '&&::-moz-placeholder': ph,
    '&&::-ms-input-placeholder': ph,
    '&&:focus::placeholder': ph,
    '&&:focus::-webkit-input-placeholder': ph,
    '&&:focus::-moz-placeholder': ph,
    '&&:focus::-ms-input-placeholder': ph,
  };
}

const typographyDefaultVariantMapping: Record<string, string> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'h6',
  subtitle2: 'h6',
  body1: 'p',
  body2: 'p',
  inherit: 'p',
  button: 'span',
  caption: 'span',
  overline: 'span',
  uiText: 'div',
};

const components: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      '*:focus-visible': theme.mixins.focusVisible(theme.palette.primary.main),
      [`table caption.${FG_TABLE_SR_ONLY_CAPTION_CLASS}`]: screenReaderOnlyLabelSx,
      [`ol.${FG_IMPLICIT_LIST_CLASS}`]: {
        margin: 0,
        padding: 0,
        listStyle: 'none',
      },
      [`ol.${FG_IMPLICIT_LIST_CLASS} > li.${FG_IMPLICIT_LIST_CLASS}`]: {
        margin: 0,
        padding: 0,
      },
    }),
  },
  MuiLink: {
    defaultProps: {
      component: LinkBehavior,
    },
  },
  MuiPickersInputBase: {
    styleOverrides: {
      sectionsContainer: ({ theme, ownerState }) => {
        if (
          ownerState?.hasStartAdornment ||
          ownerState?.isFieldFocused ||
          !ownerState?.isFieldValueEmpty
        ) {
          return {};
        }
        return {
          '&&': {
            opacity: 1,
            color: theme.palette.text.secondary,
          },
        };
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      standard: ({ theme, ownerState }) => {
        const { color, severity = 'success' } = ownerState;
        if (color) {
          return {
            backgroundColor: theme.palette.background[color],
          };
        }

        return {
          backgroundColor: theme.palette.background[severity],
        };
      },
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
      LinkComponent: LinkBehavior,
    },
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        const styles: CSSObject = {
          [`&.${MUI_STATE_CLASSES.selected}`]: {
            fontWeight: '700 !important',

            '& *': {
              fontWeight: '700 !important',
            },
          },
        };

        if (ownerState.whiteOutline) {
          styles['&:focus-visible'] = theme.mixins.focusVisible(theme.palette.common.white);
        }

        if (ownerState.rounded) {
          styles['&&'] = { borderRadius: '100px' };
        }

        return styles;
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      // MUI v9 removed the combined `outlinedPrimary` / `textPrimary` slots, so primary variants are handled in `root`.
      root: ({ theme, ownerState }) => {
        if (ownerState.variant === 'outlined' && ownerState.color === 'primary') {
          return { '&&': primaryOutlinedButtonAccessibleVars(theme) };
        }
        if (ownerState.variant === 'text' && ownerState.color === 'primary') {
          return { '&&': primaryTextButtonAccessibleVars(theme) };
        }
        return {};
      },
      outlined: ({ theme, ownerState }) => {
        if (ownerState.color === 'primary') {
          return {};
        }
        return {
          color: theme.mixins.contrastColor(ownerState.color, theme.palette.background.paper),
          '&:hover': {
            backgroundColor: theme.palette.grey[100],
          },
        };
      },
      text: ({ theme, ownerState }) => {
        if (ownerState.color === 'primary') {
          return {};
        }
        return {
          '&:hover': {
            backgroundColor: theme.palette.grey[100],
          },
        };
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        overflow: 'visible',
      },
    },
  },
  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        [`&.${MUI_STATE_CLASSES.focusVisible}`]: theme.mixins.focusVisible(
          theme.palette.primary.main
        ),
      }),
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        if (ownerState.variant === 'filled' && ownerState.color === 'default') {
          return {
            [`&& .${chipClasses.deleteIcon}`]: {
              color: '#fff !important',
              opacity: '1 !important',
              backgroundColor: 'transparent',
              '&:hover, &:focus-visible': {
                color: '#fff !important',
                opacity: '1 !important',
                backgroundColor: 'transparent',
              },
            },
          };
        }

        if (ownerState.variant === 'filled' && ownerState.color === 'primary') {
          return {
            [`& .${chipClasses.deleteIcon}`]: {
              color: theme.palette.primary.contrastText,
              opacity: 1,
              backgroundColor: 'transparent',
              '&:hover, &:focus-visible': {
                color: theme.palette.primary.contrastText,
                opacity: 1,
                backgroundColor: 'transparent',
              },
            },
          };
        }
        return {};
      },
      outlined: ({ theme, ownerState }) => {
        const gradient = GRADIENT_COLORS[ownerState.color as keyof typeof GRADIENT_COLORS];
        return gradient
          ? {
              borderColor: gradient.main,
              color: gradient.main,
            }
          : {
              color: theme.mixins.contrastColor(ownerState.color, theme.palette.background.paper),
            };
      },
    },
  },
  MuiDialog: {
    defaultProps: {
      container: getAppRootModalContainer,
      slotProps: {
        paper: {
          tabIndex: -1, // makes the paper (with role="dialog") focusable
        },
        transition: {
          onEntered: (node) => {
            const dialog = node.querySelector('[role="dialog"]');
            if (!dialog || !(dialog instanceof HTMLElement)) {
              return;
            }

            if (dialog.contains(document.activeElement)) {
              return;
            }
            focusFirstFocusable(dialog);
            if (!dialog.contains(document.activeElement)) {
              dialog.focus();
            }
          },
        },
      },
    },
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundColor: theme.palette.background.default,
      }),
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.background.default,
      }),
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`& .${inputLabelClasses.formControl}.${inputLabelClasses.outlined}.${inputLabelClasses.shrink}.${MUI_STATE_CLASSES.focused}`]:
          {
            backgroundColor: theme.palette.background.default,
            padding: `0 ${theme.spacing(0.875)}`, // 7px
            margin: `0 ${theme.spacing(-0.625)}`, // -5px
            borderRadius: theme.spacing(1),
          },
      }),
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`&:has(.${radioClasses.root}.${MUI_STATE_CLASSES.focusVisible})`]:
          theme.mixins.focusVisible(theme.palette.primary.main),
      }),
    },
  },
  MuiFormGroup: {
    styleOverrides: {
      row: ({ theme }) => ({
        [`& > .${formControlLabelClasses.root}`]: {
          paddingRight: theme.spacing(1),
        },
      }),
    },
  },
  MuiIconButton: {
    defaultProps: {
      variant: 'standard',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        '&:focus-visible': {
          ...theme.mixins.focusVisible(theme.palette.primary.main),
          zIndex: 1,
        },
      }),
    },
    variants: [
      {
        props: { variant: 'filled' },
        style: ({ theme }) => getFilledIconButtonStyles(theme),
      },
      ...COLOR_OPTIONS.map<MuiIconButtonVariant>((color) => ({
        props: { variant: 'filled', color },
        style: ({ theme }) => getFilledIconButtonStyles(theme, color),
      })),
      {
        props: { variant: 'outlined' },
        style: ({ theme }) => getOutlinedIconButtonStyles(theme),
      },
      ...COLOR_OPTIONS.map<MuiIconButtonVariant>((color) => ({
        props: { variant: 'outlined', color },
        style: ({ theme }) => getOutlinedIconButtonStyles(theme, color),
      })),
    ],
  },
  MuiInputBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`&.${MUI_STATE_CLASSES.focused}`]: theme.mixins.focusVisible(theme.palette.primary.main),
      }),
      input: ({ theme }) => ({
        '&:focus-visible': {
          outline: 'none !important',
          boxShadow: 'none !important',
        },
        ...muiInputPlaceholderAccessible(theme),
      }),
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: ({ theme, ownerState }) => ({
        ...theme.mixins.invertedColor(ownerState.color),

        '&:focus-visible': {
          zIndex: 1,
        },
      }),
    },
  },
  MuiMenu: {
    defaultProps: {
      container: getAppRootModalContainer,
    },
  },
  MuiPopover: {
    defaultProps: {
      container: getAppRootModalContainer,
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme, ownerState }) => ({
        color: theme.palette.text.primary,
        '& .MuiListItemText-primary, & .MuiListItemText-secondary, & .MuiListItemIcon-root': {
          color: 'inherit',
        },
        '& .MuiSvgIcon-root': {
          color: 'inherit',
        },
        [`&:hover:not(.${MUI_STATE_CLASSES.selected})`]: {
          color: theme.palette.text.primary,
          '& .MuiListItemText-primary, & .MuiListItemIcon-root, & .MuiSvgIcon-root': {
            color: 'inherit',
          },
          [`& .${listItemTextClasses.secondary}`]: {
            color: ACCESSIBLE_SUBTEXT_ON_INTERACTIVE,
          },
        },
        [`&.${MUI_STATE_CLASSES.focusVisible}:not(.${MUI_STATE_CLASSES.selected})`]: {
          color: theme.palette.text.primary,
          '& .MuiListItemText-primary, & .MuiListItemIcon-root, & .MuiSvgIcon-root': {
            color: 'inherit',
          },
          [`& .${listItemTextClasses.secondary}`]: {
            color: ACCESSIBLE_SUBTEXT_ON_INTERACTIVE,
          },
        },
        ...theme.mixins.invertedColor(ownerState.color),
      }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.background.default,

        [`&:not(.${outlinedInputClasses.error}) .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: `${OUTLINED_INPUT_BORDER} !important`,
        },
        [`&:not(.${outlinedInputClasses.error}):hover .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: '#9E9E9E !important',
        },
        '@media (hover: none)': {
          [`&:not(.${outlinedInputClasses.error}):hover .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: `${OUTLINED_INPUT_BORDER} !important`,
          },
        },
        [`&.${outlinedInputClasses.focused}:not(.${outlinedInputClasses.error}) .${outlinedInputClasses.notchedOutline}`]:
          {
            borderWidth: 1,
            borderColor: theme.palette.primary.main,
          },
        // The MuiInputBase root applies a focusVisible box-shadow when focused,
        // but for outlined inputs the notched border already shows focus — cancel it.
        [`&.${outlinedInputClasses.focused}`]: {
          boxShadow: 'none !important',
        },
      }),
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`&:not(.${MUI_STATE_CLASSES.error}) .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: `${OUTLINED_INPUT_BORDER} !important`,
        },
        [`&:not(.${MUI_STATE_CLASSES.error}):hover .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: '#9E9E9E !important',
        },
        '@media (hover: none)': {
          [`&:not(.${MUI_STATE_CLASSES.error}):hover .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: `${OUTLINED_INPUT_BORDER} !important`,
          },
        },
        [`& .${outlinedInputClasses.root}.${outlinedInputClasses.focused}:not(.${outlinedInputClasses.error}) .${outlinedInputClasses.notchedOutline}`]:
          {
            borderWidth: 1,
            borderColor: theme.palette.primary.main,
          },
      }),
    },
  },
  MuiPagination: {
    defaultProps: {
      siblingCount: 0,
      'aria-label': i18n.t('pagination'),
    },
    styleOverrides: {
      ul: ({ theme }) => ({
        rowGap: theme.spacing(0.5),
      }),
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`&.${MUI_STATE_CLASSES.selected}:not(.${paginationItemClasses.outlined})`]: {
          fontWeight: theme.typography.fontWeightBold,
          boxShadow: `inset 0 0 0 2px currentColor`,
        },
      }),
      outlined: ({ theme, ownerState }) => {
        if (ownerState?.color === 'primary') {
          const fg = theme.palette.primary.dark;
          const selectedBg = alpha(theme.palette.primary.main, 0.08);
          const selectedBgHover = alpha(theme.palette.primary.main, 0.12);
          const selectedVisual = {
            color: `${fg} !important`,
            border: `2px solid ${fg} !important`,
            backgroundColor: selectedBg,
            fontWeight: theme.typography.fontWeightBold,
          };
          return {
            [`&&.${MUI_STATE_CLASSES.selected}`]: {
              ...selectedVisual,
              '&:hover': {
                ...selectedVisual,
                backgroundColor: selectedBgHover,
              },
              [`&.${MUI_STATE_CLASSES.focusVisible}`]: {
                ...selectedVisual,
                backgroundColor: selectedBgHover,
              },
              [`&.${MUI_STATE_CLASSES.disabled}`]: {
                color: theme.palette.action.disabled,
                border: `2px solid ${theme.palette.action.disabledBackground}`,
                backgroundColor: theme.palette.action.selected,
                fontWeight: theme.typography.fontWeightRegular,
              },
            },
          };
        }
        return {
          [`&.${MUI_STATE_CLASSES.selected}`]: {
            color: theme.palette.text.primary,
            fontWeight: theme.typography.fontWeightBold,
            borderWidth: 2,
          },
        };
      },
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: ({ theme }) => ({
        // Only apply focus outline if NOT inside FormControlLabel
        [`&.${MUI_STATE_CLASSES.focusVisible}:not(.${formControlLabelClasses.root} *)`]:
          theme.mixins.focusVisible(theme.palette.primary.main),
      }),
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        maxWidth: '100%',
      },
    },
  },
  MuiStepIcon: {
    styleOverrides: {
      root: ({ theme }) => ({
        display: 'block',
        transition: theme.transitions.create('color', {
          duration: theme.transitions.duration.shortest,
        }),
        color: theme.palette.grey[700],
        [`&.${stepIconClasses.active}`]: {
          color: theme.palette.primary.main,
        },
        [`&.${stepIconClasses.completed}`]: {
          color: theme.palette.primary.main,
        },
        [`&.${stepIconClasses.error}`]: {
          color: theme.palette.error.main,
        },
      }),
    },
  },
  MuiSwitch: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:has(:focus-visible)': {
          ...theme.mixins.focusVisible(theme.palette.primary.main),
          borderRadius: 100,
        },
      }),
      switchBase: {
        [`&.${MUI_STATE_CLASSES.checked}+.${switchClasses.track}`]: {
          opacity: 0.85,
        },
      },
      track: {
        opacity: 0.5,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`&.${MUI_STATE_CLASSES.selected}`]: {
          boxShadow: 'none !important',
        },
        [`&.${tabClasses.textColorPrimary}.${MUI_STATE_CLASSES.selected}`]: {
          color: theme.palette.primary.dark,
          '& .MuiTypography-root': { color: 'inherit' },
        },

        '&:focus-visible': {
          boxShadow: `inset 0 0 0 3px ${theme.palette.primary.main} !important`,
          outline: 'none !important',
        },
      }),
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: ({ theme, ownerState }) => {
        const colorKey = isMuiColor(ownerState?.textColor) ? ownerState.textColor : 'primary';
        const paletteColor = theme.palette[colorKey];
        return {
          backgroundColor: colorKey === 'primary' ? paletteColor.dark : paletteColor.main,
        };
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme, ownerState }) => ({
        ...theme.mixins.invertedColor(ownerState.color),
        color: theme.palette.text.primary,

        [`&.${MUI_STATE_CLASSES.selected}`]: {
          borderColor:
            theme.palette[isMuiColor(ownerState.color) ? ownerState.color : 'primary'].main,
        },
      }),
    },
  },
  MuiTypography: {
    defaultProps: {
      variant: 'uiText',
      variantMapping: typographyDefaultVariantMapping,
    },
    styleOverrides: {
      uiText: ({ theme }) => theme.typography.body1,
      caption: ({ theme, ownerState }) => ({
        color: ownerState.color ?? theme.palette.text.secondary,
      }),
    },
  },
  'MuiTiptap-FieldContainer': {
    defaultProps: {
      variant: 'standard',
    },
    styleOverrides: {
      standard: ({ theme }) => ({
        margin: theme.spacing(1),
        padding: theme.spacing(0, 1.5),
        backgroundColor: theme.palette.background.default,
        ...theme.mixins.gradientBorder({ radius: 8 }),

        [`& .${menuButtonClasses.root}, & .${menuSelectClasses.root}`]: {
          [`&.${MUI_STATE_CLASSES.focused}`]: {
            zIndex: 1,
          },
        },

        [`&.${MUI_STATE_CLASSES.error}`]: {
          ...theme.mixins.gradientBorder({ variant: 'red', radius: 8 }),

          [`&:has(.${HTML_EDITOR_CLASSES.contentFocused})`]: theme.mixins.focusVisible(
            theme.palette.error.main
          ),
        },

        [`&:has(.${HTML_EDITOR_CLASSES.contentFocused})`]: theme.mixins.focusVisible(
          theme.palette.primary.main
        ),
      }),
    },
  },
  'MuiTiptap-RichTextContent': {
    styleOverrides: {
      root: ({ theme }) => ({
        [`& > .${HTML_EDITOR_CLASSES.content}`]: {
          ...theme.typography.body2,

          '& p:not(:last-child)': {
            marginBlockEnd: theme.spacing(1),
          },

          [`& .${HTML_EDITOR_CLASSES.trailingBreak}`]: {
            display: 'none',
          },
        },
      }),
      editable: ({ theme }) => ({
        padding: theme.spacing(1, 0),

        [`& > .${HTML_EDITOR_CLASSES.content}`]: {
          minHeight: 60,
        },

        [`& > .${HTML_EDITOR_CLASSES.contentFocused}`]: {
          outline: 'none !important',
        },
      }),
      readonly: {
        // For when HtmlEditor is in readonly mode, but not in RichTextReadOnly
        [`&.${MUI_STATE_CLASSES.disabled} > .${HTML_EDITOR_CLASSES.content}`]: {
          minHeight: 60,
        },
      },
    },
  },
  MuiSnackbarContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        // Ensure notistack variants use our accessible theme colors
        '&[class*="variantSuccess"]': {
          backgroundColor: `${theme.palette.success.main} !important`,
          color: `${theme.palette.success.contrastText} !important`,
        },
        '&[class*="variantError"]': {
          backgroundColor: `${theme.palette.error.main} !important`,
          color: `${theme.palette.error.contrastText} !important`,
        },
        '&[class*="variantWarning"]': {
          backgroundColor: `${theme.palette.warning.main} !important`,
          color: `${theme.palette.warning.contrastText} !important`,
        },
        '&[class*="variantInfo"]': {
          backgroundColor: `${theme.palette.info.main} !important`,
          color: `${theme.palette.info.contrastText} !important`,
        },
      }),
    },
  },
};

const PRIMARY_SELECTED = '#007BB914';
const SECONDARY_DARK = '#2E3539';
const SECONDARY_TEXT_COLOR = '#666666';
const BORDER_COLOR = '#C0C0C0';
const PAPER_COLOR = '#F9FBFC';

const SOFT_CARD_SHADOW = '-1px 2px 4px 2px rgba(0, 0, 0, 0.1)';

const baseTheme = createTheme({
  palette: {
    primary: {
      main: '#00579d',
      dark: '#00457d',
      _states: { selected: PRIMARY_SELECTED },
    },
    secondary: { main: blueGrey[700], dark: SECONDARY_DARK },
    info: {
      main: blue[700], // #1976d2, contrast 4.72:1
      dark: blue[800], // #1565c0, contrast 6.5:1
    },
    success: {
      main: green[800], // #2e7d32, contrast 5.4:1
      dark: green[900], // #1b5e20, contrast 7.9:1
    },
    warning: {
      main: orange[900], // #e65100, contrast 4.6:1
    },
    error: {
      main: red[800], // #c62828, contrast 6.13:1
      dark: red[900], // #b71c1c, contrast 8.16:1
    },
    text: { secondary: SECONDARY_TEXT_COLOR },
    divider: BORDER_COLOR,
    background: {
      paper: PAPER_COLOR,
    },
  },
  borders: {
    default: `1px solid ${BORDER_COLOR}`,
  },
  customShadows: {
    soft: SOFT_CARD_SHADOW,
  },
  components,
});

export const theme = augmentTheme(baseTheme);
